import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { KeyRound, Lock, Mail, ShieldCheck, AlertCircle } from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { useAdminI18n } from "@/lib/adminI18n";

export default function AdminLoginPage() {
  const { login, isAuthenticated } = useAdminAuth();
  const { t, lang, setLang, dir, isRTL } = useAdminI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState("login");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  if (isAuthenticated) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setBusy(true);
    try {
      if (mode === "forgot") {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: email.trim() || undefined }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.detail || t("login.resetRequestFailed"));
        setMode("reset");
        setNotice(data?.message || t("login.resetCodeSent"));
        return;
      }
      if (mode === "reset") {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password-code`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: email.trim(), code, new_password: newPassword }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.detail || t("login.resetFailed"));
        setMode("login");
        setPassword("");
        setCode("");
        setNewPassword("");
        setNotice(t("login.resetComplete"));
        return;
      }
      await login(email.trim(), password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(err?.message || t("login.loginFailed"));
    } finally {
      setBusy(false);
    }
  };

  const isResetMode = mode === "reset";
  const isForgotMode = mode === "forgot";

  return (
    <main
      dir={dir}
      className="flex min-h-screen items-center justify-center bg-[#080808] px-4 py-8 text-white sm:px-6"
    >
      <form
        onSubmit={onSubmit}
        autoComplete="off"
        className="w-full max-w-md rounded-[2rem] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.025)),rgba(10,10,10,0.92)] p-8 shadow-[0_30px_80px_rgba(0,0,0,0.38)] backdrop-blur-xl"
        data-testid="admin-login-form"
      >
        <div className={`mb-1 flex items-center gap-3 ${isRTL ? "flex-row-reverse text-right" : ""}`}>
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-[#F37021]/25 bg-[#F37021]/16 text-[#F58D4A] shadow-[0_18px_40px_rgba(243,112,33,0.18)]">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className={`mb-3 flex ${isRTL ? "justify-start" : "justify-end"}`}>
              <div className={`inline-flex rounded-full border border-white/10 bg-white/[0.04] p-1 ${isRTL ? "flex-row-reverse" : ""}`}>
                <button
                  type="button"
                  onClick={() => setLang("en")}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold ${lang === "en" ? "bg-[#F37021] text-white" : "text-white/72 hover:text-white"}`}
                >
                  {t("shell.english")}
                </button>
                <button
                  type="button"
                  onClick={() => setLang("ar")}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold ${lang === "ar" ? "bg-[#F37021] text-white" : "text-white/72 hover:text-white"}`}
                >
                  {t("shell.arabic")}
                </button>
              </div>
            </div>
            <h1 className="font-display text-xl font-bold">
              {isResetMode ? t("login.resetTitle") : isForgotMode ? t("login.forgotTitle") : t("login.title")}
            </h1>
            <p className="text-xs text-white/50">
              {isResetMode ? t("login.resetSubtitle") : isForgotMode ? t("login.forgotSubtitle") : t("login.subtitle")}
            </p>
          </div>
        </div>

        <p className={`mt-5 text-sm leading-6 text-white/55 ${isRTL ? "text-right" : ""}`}>
          {isResetMode ? t("login.resetIntro") : isForgotMode ? t("login.forgotIntro") : t("login.intro")}
        </p>

        <div className="mt-7 space-y-4">
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/60">
              {t("login.email")}
            </span>
            <div className="relative mt-1.5">
              <Mail className={`absolute top-1/2 h-4 w-4 -translate-y-1/2 text-white/40 ${isRTL ? "right-3" : "left-3"}`} />
              <input
                type="email"
                name="bob-admin-email"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`h-11 w-full rounded-2xl border border-white/10 bg-black/40 text-sm focus:border-[#F37021] focus:outline-none ${isRTL ? "pr-10 pl-3 text-right" : "ps-10 pe-3"}`}
                placeholder=""
                data-testid="admin-login-email"
              />
            </div>
          </label>

          {mode === "login" && (
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/60">
                {t("login.password")}
              </span>
              <div className="relative mt-1.5">
                <Lock className={`absolute top-1/2 h-4 w-4 -translate-y-1/2 text-white/40 ${isRTL ? "right-3" : "left-3"}`} />
                <input
                  type="password"
                  name="bob-admin-password"
                  autoComplete="new-password"
                  required
                  minLength={1}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`h-11 w-full rounded-2xl border border-white/10 bg-black/40 text-sm focus:border-[#F37021] focus:outline-none ${isRTL ? "pr-10 pl-3 text-right" : "ps-10 pe-3"}`}
                  placeholder=""
                  data-testid="admin-login-password"
                />
              </div>
            </label>
          )}

          {mode === "reset" && (
            <>
              <label className="block">
                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/60">
                  {t("login.resetCode")}
                </span>
                <div className="relative mt-1.5">
                  <KeyRound className={`absolute top-1/2 h-4 w-4 -translate-y-1/2 text-white/40 ${isRTL ? "right-3" : "left-3"}`} />
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className={`h-11 w-full rounded-2xl border border-white/10 bg-black/40 text-sm focus:border-[#F37021] focus:outline-none ${isRTL ? "pr-10 pl-3 text-right" : "ps-10 pe-3"}`}
                    data-testid="admin-reset-code"
                  />
                </div>
              </label>
              <label className="block">
                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/60">
                  {t("login.newPassword")}
                </span>
                <div className="relative mt-1.5">
                  <Lock className={`absolute top-1/2 h-4 w-4 -translate-y-1/2 text-white/40 ${isRTL ? "right-3" : "left-3"}`} />
                  <input
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={8}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={`h-11 w-full rounded-2xl border border-white/10 bg-black/40 text-sm focus:border-[#F37021] focus:outline-none ${isRTL ? "pr-10 pl-3 text-right" : "ps-10 pe-3"}`}
                    data-testid="admin-new-password"
                  />
                </div>
              </label>
            </>
          )}
        </div>

        {notice && (
          <div className={`mt-4 rounded-2xl border border-emerald-400/30 bg-emerald-400/10 p-3 text-sm text-emerald-200 ${isRTL ? "text-right" : ""}`}>
            {notice}
          </div>
        )}

        {error && (
          <div
            className={`mt-4 inline-flex items-start gap-2 rounded-2xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300 ${isRTL ? "flex-row-reverse text-right" : ""}`}
            data-testid="admin-login-error"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={busy || !email || (mode === "login" && !password) || (mode === "reset" && (!code || !newPassword))}
          className="mt-6 h-12 w-full rounded-full bg-[#F37021] font-semibold text-white transition-colors hover:bg-[#E06015] disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="admin-login-submit"
        >
          {busy
            ? t("login.processing")
            : mode === "forgot"
              ? t("login.sendResetCode")
              : mode === "reset"
                ? t("login.resetPassword")
                : t("login.signIn")}
        </button>

        <div className={`mt-4 flex items-center justify-center gap-3 text-xs ${isRTL ? "flex-row-reverse" : ""}`}>
          {mode === "login" ? (
            <button type="button" onClick={() => { setError(""); setNotice(""); setMode("forgot"); }} className="font-semibold text-[#F58D4A] hover:text-[#FFB27D]">
              {t("login.forgotPassword")}
            </button>
          ) : (
            <button type="button" onClick={() => { setError(""); setNotice(""); setMode("login"); }} className="font-semibold text-[#F58D4A] hover:text-[#FFB27D]">
              {t("login.backToLogin")}
            </button>
          )}
          {mode === "reset" && (
            <button type="button" onClick={() => { setError(""); setMode("forgot"); }} className="font-semibold text-white/60 hover:text-white">
              {t("login.resendCode")}
            </button>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-white/40">{t("login.restricted")}</p>
      </form>
    </main>
  );
}
